import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const MyUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await usersAPI.getMyUsers(); // Use the new agent-specific endpoint
      
      // Handle response structure: res.data.data for paginated response, res.data for direct array
      const usersArr = Array.isArray(res.data) ? res.data 
        : Array.isArray(res.data?.data) ? res.data.data 
        : [];
      setUsers(usersArr);
    } catch (error) {
      console.error('Fetch users error:', error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // The AuthContext will handle the redirect
        return;
      }
      
      // Handle server errors gracefully
      if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
        setUsers([]);
        return;
      }
      
      // Handle other errors
      if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to view users.');
        setUsers([]);
        return;
      }
      
      toast.error('Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (userData) => {
    try {
      if (editingUser) {
        await usersAPI.updateUser(editingUser._id, userData);
        toast.success('User updated successfully');
      } else {
        await usersAPI.createUser(userData);
        toast.success('User created successfully');
      }
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Save user error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save user';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">My Users</h2>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          onClick={() => { setEditingUser(null); setShowModal(true); }}
        >
          + Add User
        </button>
      </div>
      <table className="w-full border rounded-lg mb-4">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2">Name</th>
            <th className="p-2">Email</th>
            <th className="p-2">Phone</th>
            <th className="p-2">Role</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="p-4 text-center text-gray-500">
                Loading users...
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan="5" className="p-4 text-center text-gray-500">
                No users found. Create your first user!
              </td>
            </tr>
          ) : (
            users.map(u => (
              <tr key={u._id} className="border-t">
                <td className="p-2">{u.name}</td>
                <td className="p-2">{u.email}</td>
                <td className="p-2">{u.phone}</td>
                <td className="p-2">{u.role}</td>
                <td className="p-2">
                  <button
                    className="px-2 py-1 bg-yellow-500 text-white rounded mr-2"
                    onClick={() => { setEditingUser(u); setShowModal(true); }}
                  >Edit</button>
                  {/* No delete button for agents */}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {showModal && (
        <UserModal
          user={editingUser}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
};

const UserModal = ({ user, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    password: '',
    role: user?.role || 'user',
    isActive: user?.isActive ?? true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (!form.phone || form.phone.length !== 10 || !/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error('Please enter a valid 10-digit phone number starting with 6-9');
      return;
    }
    
    setIsSubmitting(true);
    await onSave(form);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">{user ? 'Edit User' : 'Add User'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            className="w-full border rounded p-2"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Name"
            required
          />
          <input
            className="w-full border rounded p-2"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            type="email"
            required
          />
          <input
            className="w-full border rounded p-2"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Phone (10 digits starting with 6-9)"
            type="tel"
            pattern="[6-9][0-9]{9}"
            maxLength="10"
            required
          />
          {!user && (
            <input
              className="w-full border rounded p-2"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Password"
              type="password"
              required
            />
          )}
          <div className="flex justify-end space-x-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button type="submit" className={`px-4 py-2 rounded-lg text-white ${isSubmitting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (user ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyUsers; 